// aluno/oratoria-render.js — funções de renderização do portal oratória.
//
// Módulo auxiliar extraído de oratoria.js para manter cada arquivo < 400 linhas.
//
// Origem (monólito): linhas 5756-5948 (renderOratoria, openOratoriaModal e
//   funções de calendário da oratoriaScreen).
//
// Dependências:
//   - router (core/router.js) — router.go() para navegação de volta
//
// Exporta:
//   function renderizarProgramacao(container, aulas)
//   function htmlItemAula(aula, isProxima)

// ── Constantes de formatação ──────────────────────────────────────────────────

const MESES_NOMES = [
  '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const TURNOS_LABEL = {
  manha: 'Manhã',
  tarde: 'Tarde',
  noite: 'Noite',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Formata data ISO "YYYY-MM-DD" para exibição BR.
 * @param {string} iso
 * @returns {{ dia:number, mes:number, mesNome:string, ano:number, dataStr:string }}
 */
export function parsearDataISO(iso) {
  const [ano, mes, dia] = iso.split('-').map(Number);
  return {
    dia,
    mes,
    ano,
    mesNome:  MESES_NOMES[mes] || '',
    dataStr:  `${String(dia).padStart(2,'0')}/${String(mes).padStart(2,'0')}/${ano}`,
    dateObj:  new Date(ano, mes - 1, dia),
  };
}

/**
 * Retorna iniciais de um nome (máx. 2 palavras).
 * @param {string} nome
 * @returns {string}
 */
function _iniciais(nome) {
  return (nome || '?').split(' ').slice(0, 2).map(n => n[0] || '').join('').toUpperCase() || '?';
}

/**
 * Gera HTML do avatar de professor (sem foto, apenas iniciais).
 * @param {string} nome
 * @param {string} [tamanho='40px']
 * @returns {string}
 */
function _avatarProfessor(nome, tamanho = '40px') {
  const ini = _iniciais(nome);
  return `<div style="width:${tamanho};height:${tamanho};border-radius:50%;background:linear-gradient(135deg,#6A001F,#B3002D);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;color:#fff;flex-shrink:0">${ini}</div>`;
}

// ── Renderização de um card de aula ──────────────────────────────────────────

/**
 * Gera o HTML de um card de aula para a lista principal.
 * @param {object} aula - objeto da programação (data, materia, professor, turno, dia_semana)
 * @param {boolean} isProxima - se é a próxima aula do aluno
 * @returns {string} HTML
 */
export function htmlItemAula(aula, isProxima = false) {
  const { dia, mes, mesNome } = parsearDataISO(aula.data);
  const turnoLabel = TURNOS_LABEL[aula.turno] || aula.turno || '';
  const diaFormatado = aula.dia_semana
    ? aula.dia_semana.charAt(0).toUpperCase() + aula.dia_semana.slice(1)
    : '';

  return `
    <div class="orat-card${isProxima ? ' orat-card-proxima' : ''}">
      ${isProxima ? '<div class="orat-proxima-badge">Próxima aula</div>' : ''}
      <div class="orat-card-acento"></div>
      <div class="orat-card-date">
        <div class="orat-card-day">${dia}</div>
        <div class="orat-card-month">${mesNome.substring(0,3).toUpperCase()}</div>
      </div>
      <div class="orat-card-body">
        <div class="orat-card-materia">${aula.materia || 'A definir'}</div>
        <div class="orat-card-meta">
          <span class="orat-card-meta-item">📅 ${diaFormatado} · ${turnoLabel}</span>
        </div>
        <div class="orat-card-prof-row">
          ${_avatarProfessor(aula.professor, '28px')}
          <span class="orat-card-prof-nome">${aula.professor || 'Professor a definir'}</span>
        </div>
      </div>
    </div>`;
}

// ── Renderização da lista agrupada por mês ────────────────────────────────────

/**
 * Renderiza a programação filtrada dentro do container.
 * Agrupa por mês, marca a próxima aula futura.
 *
 * @param {HTMLElement} container - raiz do componente (deve conter #orat-main)
 * @param {Array<object>} aulas   - aulas filtradas para a turma do aluno
 */
export function renderizarProgramacao(container, aulas) {
  const main = container.querySelector('#orat-main');
  if (!main) return;

  if (!aulas.length) {
    main.innerHTML = `
      <div style="text-align:center;padding:60px 20px;color:#9B9B9B">
        <div style="font-size:36px;margin-bottom:12px">📅</div>
        <div style="font-size:15px;font-weight:700">Nenhuma aula programada para sua turma.</div>
        <div style="font-size:13px;margin-top:8px">Consulte a secretaria para mais informações.</div>
      </div>`;
    return;
  }

  // Determinar próxima aula (primeira futura ou hoje)
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  let proxima = aulas.find(a => {
    const { dateObj } = parsearDataISO(a.data);
    return dateObj >= hoje;
  });

  // Agrupar por mês "YYYY-MM"
  const grupos = {};
  aulas.forEach(a => {
    const chave = a.data.substring(0, 7); // "YYYY-MM"
    if (!grupos[chave]) grupos[chave] = [];
    grupos[chave].push(a);
  });

  let html = '';
  Object.keys(grupos).sort().forEach(chave => {
    const lista = grupos[chave];
    const [ano, mes] = chave.split('-').map(Number);
    const nomeMes = `${MESES_NOMES[mes]} ${ano}`;

    html += `
      <div class="orat-mes-section">
        <div class="orat-mes-heading">
          <span class="orat-mes-titulo">${nomeMes}</span>
          <span class="orat-mes-badge">${lista.length} aula${lista.length > 1 ? 's' : ''}</span>
          <div class="orat-mes-linha"></div>
        </div>
        ${lista.map(a => htmlItemAula(a, a === proxima)).join('')}
      </div>`;
  });

  main.innerHTML = html;
}

// ── Estilos scoped ────────────────────────────────────────────────────────────

/**
 * Injeta CSS específico de oratória no <head> (idempotente).
 */
export function injetarEstilos() {
  if (document.getElementById('orat-styles')) return;
  const el = document.createElement('style');
  el.id = 'orat-styles';
  el.textContent = `
    .orat-card{position:relative;background:#fff;border-radius:16px;box-shadow:0 2px 12px rgba(0,0,0,0.08);padding:16px;margin-bottom:12px;display:flex;gap:14px;align-items:flex-start;overflow:hidden;transition:box-shadow 0.2s}
    .orat-card:hover{box-shadow:0 4px 20px rgba(0,0,0,0.13)}
    .orat-card-proxima{border:2px solid #B3002D}
    .orat-proxima-badge{position:absolute;top:10px;right:10px;font-size:9px;font-weight:800;background:linear-gradient(90deg,#6A001F,#B3002D);color:#fff;padding:3px 10px;border-radius:100px;letter-spacing:0.5px;text-transform:uppercase}
    .orat-card-acento{position:absolute;left:0;top:0;width:3px;height:100%;background:linear-gradient(180deg,#6A001F,#B3002D);border-radius:3px 0 0 3px}
    .orat-card-date{display:flex;flex-direction:column;align-items:center;justify-content:center;min-width:44px;padding:8px 0}
    .orat-card-day{font-size:24px;font-weight:900;color:#6A001F;line-height:1}
    .orat-card-month{font-size:10px;font-weight:800;color:#9B9B9B;letter-spacing:1px}
    .orat-card-body{flex:1;min-width:0}
    .orat-card-materia{font-size:15px;font-weight:800;color:#0A0A0A;margin-bottom:6px;line-height:1.3}
    .orat-card-meta{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px}
    .orat-card-meta-item{font-size:12px;font-weight:600;color:#6B6B6B}
    .orat-card-prof-row{display:flex;align-items:center;gap:8px}
    .orat-card-prof-nome{font-size:12px;font-weight:700;color:#6A001F}
    .orat-mes-section{margin-bottom:32px}
    .orat-mes-heading{display:flex;align-items:center;gap:12px;margin-bottom:14px}
    .orat-mes-titulo{font-size:18px;font-weight:800;color:#6A001F;letter-spacing:-0.3px}
    .orat-mes-badge{font-size:11px;font-weight:700;color:#fff;background:linear-gradient(90deg,#6A001F,#B3002D);padding:3px 10px;border-radius:100px;white-space:nowrap}
    .orat-mes-linha{flex:1;height:1px;background:linear-gradient(90deg,rgba(106,0,31,0.2),transparent)}
  `;
  document.head.appendChild(el);
}
